"use client"

import BoringAvatar from "boring-avatars"
import type { ReactNode } from "react"

import {
  Avatar as UUIAvatar,
  type AvatarProps as UUIAvatarProps,
} from "@/components/ui-kit/untitled/base/avatar/avatar"

/**
 * App-wide identity avatar. Consumes the server's standard identity shape
 * — `name` plus an optional `avatar_url` — and renders a deterministic
 * `boring-avatars` beam as the fallback when no uploaded avatar is
 * available. This is the single component every surface should use for a
 * person-like avatar.
 *
 * The server-side identity shape it expects is already ubiquitous:
 *   - `UserResource`  → avatar_url, name
 *   - `ProfileResource` → same
 *   - `TrackSuggestionResource` → contributor_name + contributor_avatar_url
 *     (pre-resolved for guest contributors too)
 *
 * Internally delegates to the UUI Avatar for sizing / outline / badge /
 * status / verified semantics, swapping only the fallback content.
 */

type AvatarSize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl"

export interface AvatarProps
  extends Pick<
    UUIAvatarProps,
    | "className"
    | "status"
    | "verified"
    | "badge"
    | "focusable"
    | "contrastBorder"
  > {
  /** Display name — used as alt text and as the deterministic boring-avatars seed. */
  name: string
  /** Server-shipped avatar URL (e.g. `user.avatar_url`). */
  avatarUrl?: string | null
  size?: AvatarSize
}

/** Pixel sizes paired with UUI's token sizes — used to size the boring-avatars SVG. */
const SIZE_PX: Record<AvatarSize, number> = {
  xxs: 16,
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 56,
  "2xl": 64,
}

/** Batyr brand palette for identity avatars. */
const AVATAR_PALETTE = [
  "#FBC845", // batyr yellow
  "#30DAC4", // batyr mint
  "#3B5C85", // batyr blue (denim)
  "#0B2555", // batyr navy
  "#FCF8EC", // batyr cream
]

/**
 * The deterministic identity fallback, exported so sibling wrappers
 * (e.g. `AvatarLabelGroup`) can inject the same beam into UUI components
 * that take a raw `placeholder` prop.
 */
export function identityPlaceholder(name: string, size: AvatarSize): ReactNode {
  return (
    <BoringAvatar
      size={SIZE_PX[size]}
      name={name}
      variant="beam"
      colors={AVATAR_PALETTE}
    />
  )
}

export function Avatar({ name, avatarUrl, size = "md", ...rest }: AvatarProps) {
  const placeholder = identityPlaceholder(name, size)

  return (
    <UUIAvatar
      src={avatarUrl ?? undefined}
      alt={name}
      size={size}
      placeholder={placeholder}
      {...rest}
    />
  )
}
